import { Component, signal, type OnDestroy, type OnInit } from '@angular/core';
import { MessageModule } from 'primeng/message';
import type { ExtendedNetworkInformation, MessageSeverity } from './interfaces/network-status-banner-types';

@Component({
    selector: 'network-status-banner',
    imports: [MessageModule],
    templateUrl: './network-status-banner.html',
    styleUrl: './network-status-banner.css'
})
export class NetworkStatusBanner implements OnInit, OnDestroy {
    public readonly showBanner = signal(false);
    public readonly message = signal('');
    public readonly severity = signal<MessageSeverity>('warn');
    private readonly updateNetworkState = (): void => this.evaluateNetworkState();

    public ngOnInit(): void {
        window.addEventListener('online', this.updateNetworkState);
        window.addEventListener('offline', this.updateNetworkState);

        const connection = this.getConnectionInfo();
        connection?.addEventListener('change', this.updateNetworkState);

        this.evaluateNetworkState();
    }

    public ngOnDestroy(): void {
        window.removeEventListener('online', this.updateNetworkState);
        window.removeEventListener('offline', this.updateNetworkState);

        const connection = this.getConnectionInfo();
        connection?.removeEventListener('change', this.updateNetworkState);
    }

    private getConnectionInfo(): ExtendedNetworkInformation | null {
        const navigatorWithConnection = navigator as Navigator & {
            connection?: ExtendedNetworkInformation;
            mozConnection?: ExtendedNetworkInformation;
            webkitConnection?: ExtendedNetworkInformation;
        };

        return (
            navigatorWithConnection.connection
            ?? navigatorWithConnection.mozConnection
            ?? navigatorWithConnection.webkitConnection
            ?? null
        );
    }

    private evaluateNetworkState(): void {
        if (!navigator.onLine) {
            this.showBanner.set(true);
            this.severity.set('error');
            this.message.set('Sin conexion a internet. Revisa tu red para continuar.');
            return;
        }

        const connection = this.getConnectionInfo();
        const effectiveType = connection?.effectiveType;
        const downlink = connection?.downlink ?? 0;
        const connectionDetail = this.buildConnectionDetail(connection);

        const isSlowConnection =
            effectiveType === 'slow-2g'
            || effectiveType === '2g'
            || effectiveType === '3g'
            || (downlink > 0 && downlink < 1.5);

        if (isSlowConnection) {
            this.showBanner.set(true);
            this.severity.set('warn');
            this.message.set(`Conexion actual: ${connectionDetail}. Tu conexion es inestable o lenta (2G/3G). Algunas funciones pueden tardar mas de lo normal.`);
            return;
        }

        this.showBanner.set(false);
        this.severity.set('warn');
        this.message.set('');
    }

    private buildConnectionDetail(connection: ExtendedNetworkInformation | null): string {
        if (!connection) {
            return 'No disponible en este navegador';
        }

        const typeLabel = this.getTypeLabel(connection.effectiveType);
        const downlinkLabel =
            connection.downlink && connection.downlink > 0
                ? `${connection.downlink.toFixed(1)} Mbps`
                : 'Mbps no disponible';
        const rttLabel =
            connection.rtt && connection.rtt > 0
                ? `${Math.round(connection.rtt)} ms`
                : 'latencia no disponible';

        return `${typeLabel} | ${downlinkLabel} | ${rttLabel}`;
    }

    private getTypeLabel(type?: string): string {
        if (!type) {
            return 'Tipo no disponible';
        }

        return type.toUpperCase();
    }
}
